import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { styled } from '@linaria/react'
import { motion, AnimatePresence } from 'framer-motion'
import { MinimalAlert, MinimalButton } from '@ovasabi/ui-minimal'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { omitTransientProps } from '../../lib/omitTransientProps'
import type { AppNotification } from './events'
import { IncomingOrderAlert } from './IncomingOrderAlert'

const Style = {
  Container: styled.aside`
    position: fixed;
    top: calc(env(safe-area-inset-top, 0px) + 16px);
    right: 16px;
    z-index: 10000;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: min(calc(100vw - 32px), 400px);
    pointer-events: none;
  `,
  Toast: styled(omitTransientProps(motion.div))`
    pointer-events: auto;
    width: 100%;
    background: ${minimalVars.color.bgSurface};
    border-radius: ${minimalVars.radius.md};
    box-shadow: 0 8px 24px rgba(23, 39, 62, 0.16);
  `,
  // The standing new-order alert; it is empty for everyone but a kitchen
  // with a paid order waiting.
  Standing: styled.div`
    pointer-events: auto;
    border-radius: ${minimalVars.radius.md};
    box-shadow: 0 8px 24px rgba(23, 39, 62, 0.16);
    background: ${minimalVars.color.bgSurface};
    &:empty {
      display: none;
    }
  `,
  Actions: styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  `,
}

export const AppNotificationBanner = () => {
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const navigate = useNavigate()

  useEffect(() => {
    const handleNotification = (e: Event) => {
      const detail = (e as CustomEvent<AppNotification>).detail
      if (!detail || !detail.title) return

      // A kitchen's new paid order has the standing alert above the toasts,
      // which stays until the order is accepted; a timed toast would repeat it.
      if (detail.cue !== 'new_order') setNotifications((prev) => {
        // Prevent duplicate keys
        if (prev.some((n) => n.key === detail.key)) return prev
        const current = prev.filter(item => !detail.groupKey || item.groupKey !== detail.groupKey)
        return [...current.slice(-1), detail]
      })

      // Native browser notification when supported and in background
      if (
        typeof window !== 'undefined' &&
        'Notification' in window &&
        Notification.permission === 'granted' &&
        document.visibilityState !== 'visible'
      ) {
        try {
          new Notification(detail.title, {
            body: detail.body,
            icon: '/notification-icon-192.png',
          })
        } catch {
          /* Ignore blocked notifications */
        }
      }
    }

    window.addEventListener('chow:app-notification', handleNotification)
    return () => window.removeEventListener('chow:app-notification', handleNotification)
  }, [])

  // Auto-dismiss top notification after 7 seconds
  useEffect(() => {
    if (notifications.length === 0) return
    const timer = setTimeout(() => {
      setNotifications((prev) => prev.slice(1))
    }, 7000)
    return () => clearTimeout(timer)
  }, [notifications])

  const dismiss = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }

  const activate = (item: AppNotification) => {
    setNotifications((prev) => prev.filter((n) => n.id !== item.id))
    if (item.url) navigate(item.url)
  }

  return (
    <Style.Container aria-live="polite" role="region" aria-label="Notifications">
      <Style.Standing>
        <IncomingOrderAlert />
      </Style.Standing>
      <AnimatePresence>
        {notifications.map((item) => (
          <Style.Toast
            key={item.id}
            data-notification-tone={item.tone ?? 'info'}
            initial={{ opacity: 0, y: -16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.15 } }}
          >
            <MinimalAlert tone={item.tone ?? 'info'} title={item.title}>
              {item.body}
              <Style.Actions>
                {item.url && <MinimalButton variant="ghost" tone={item.tone ?? 'info'} size="sm" onClick={() => activate(item)}>
                  {item.cue === 'message' ? 'Open message' : 'View order'}
                </MinimalButton>}
                <MinimalButton variant="ghost" tone={item.tone ?? 'info'} size="sm" onClick={() => dismiss(item.id)} aria-label="Dismiss notification">
                  Dismiss
                </MinimalButton>
              </Style.Actions>
            </MinimalAlert>
          </Style.Toast>
        ))}
      </AnimatePresence>
    </Style.Container>
  )
}
